import React, { createContext, type ReactNode, useContext, useMemo } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import type { ConversationAction } from '../../types';

/**
 * Context for providing getActions and prioritizeCustomActionResults to conversation actions.
 * This allows parent components to supply custom actions without prop drilling.
 */
export type ActionsContextValue = {
	/**
	 * Function to fetch custom conversation actions.
	 * When provided, these actions will be displayed in the conversation actions UI.
	 */
	getActions?: () => Promise<ConversationAction[]>;

	/**
	 * When true, only custom actions from getActions will be displayed,
	 * with static starters used as backfill if fewer than 3 actions are provided.
	 * When false, custom actions are prepended to the default conversation starters.
	 */
	prioritizeCustomActionResults?: boolean;

	/**
	 * Number of starter actions to show in the conversation actions UI.
	 * Defaults to 3. Can be specified to any number.
	 */
	numStartersToShow?: number;
};

const ActionsContext = createContext<ActionsContextValue | undefined>(undefined);

export type ActionsProviderProps = ActionsContextValue & {
	children: ReactNode;
};

/**
 * Provider component that supplies getActions and prioritizeCustomActionResults to descendant components.
 *
 * @example
 * ```tsx
 * <ActionsProvider
 *   getActions={async () => [...customActions]}
 *   prioritizeCustomActionResults={true}
 *   numStartersToShow={5}
 * >
 *   <RovoConversationActions {...props} />
 * </ActionsProvider>
 * ```
 */
export const ActionsProvider = ({
	children,
	getActions,
	prioritizeCustomActionResults,
	numStartersToShow,
}: ActionsProviderProps): React.JSX.Element => {
	const isRenderStabilizationEnabled =
		FeatureGates.getExperimentValue<boolean>(
			'rovo_ttai_segment_render_stabilization',
			'isEnabled',
			false,
		) === true;
	const stabilizedValue: ActionsContextValue | undefined = useMemo(
		() =>
			isRenderStabilizationEnabled
				? {
						getActions,
						prioritizeCustomActionResults,
						numStartersToShow,
					}
				: undefined,
		[getActions, isRenderStabilizationEnabled, prioritizeCustomActionResults, numStartersToShow],
	);
	const value: ActionsContextValue = stabilizedValue ?? {
		getActions,
		prioritizeCustomActionResults,
		numStartersToShow,
	};

	return <ActionsContext.Provider value={value}>{children}</ActionsContext.Provider>;
};

/**
 * Hook to consume the actions context.
 * Returns the getActions function, prioritizeCustomActionResults flag, and numStartersToShow from the nearest ActionsProvider.
 *
 * @returns The actions context value containing getActions, prioritizeCustomActionResults, and numStartersToShow.
 * If no provider is found, returns an object with undefined values.
 *
 * @example
 * ```tsx
 * const { getActions, prioritizeCustomActionResults, numStartersToShow } = useActionsContext();
 * ```
 */
export const useActionsContext = (): ActionsContextValue => {
	const context = useContext(ActionsContext);

	// Return empty object if no provider found (allows optional usage)
	return (
		context ?? {
			getActions: undefined,
			prioritizeCustomActionResults: false,
			numStartersToShow: 3,
		}
	);
};
